import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { DeepCamelCase } from '@/types/utils'
import { formatApiDate } from '@/utils/format-date'
import { classAbsenceStatus, requiredString } from '@/utils/zod'

import { type ClassAbsenceStatus } from '../constants/absences-statuses'
import type { ClassAbsencePresence } from '../types'

const attendanceStatus = z.union([
  z.literal('absent_unexcused'),
  z.literal('absent_excused'),
  z.literal('')
])

const courseSchema = z.object({
  id: requiredString(),
  name: requiredString()
})

const lessonSchema = z.object({
  id: requiredString(),
  date: z.coerce.date(),
  start_time: z.string(),
  end_time: z.string(),
  course: courseSchema
})

const absenceSchema = z.object({
  status: classAbsenceStatus,
  excused_by: z
    .object({
      id: requiredString(),
      name: requiredString(),
      is_parent: z.boolean()
    })
    .nullable(),
  excused_at: z.string().nullable()
})

const attendanceSchema = z.object({
  id: requiredString(),
  student: z.object({
    id: requiredString(),
    name: requiredString()
  }),
  presence: attendanceStatus,
  lesson: lessonSchema,
  absence: absenceSchema
})

const responseSchema = z.array(attendanceSchema)

type APIResponse = z.infer<typeof responseSchema>
type ResponseItem = z.infer<typeof attendanceSchema>
export type ClassAttendance = DeepCamelCase<ResponseItem>

const parseResponse = (response: APIResponse): ClassAttendance[] =>
  response.map(item => ({
    id: item.id,
    student: item.student,
    presence: item.presence,
    lesson: {
      id: item.lesson.id,
      date: item.lesson.date,
      startTime: item.lesson.start_time,
      endTime: item.lesson.end_time,
      course: {
        id: item.lesson.course.id,
        name: item.lesson.course.name
      }
    },
    absence: {
      status: item.absence.status,
      excusedBy: item.absence.excused_by
        ? {
            id: item.absence.excused_by.id,
            name: item.absence.excused_by.name,
            isParent: item.absence.excused_by.is_parent
          }
        : null,
      excusedAt: item.absence.excused_at
    }
  }))

type Params = {
  semester: string
  presence: ClassAbsencePresence
  status?: ClassAbsenceStatus[]
  courseId?: string[]
  classId?: string[]
  studentId?: string[]
  dateBefore?: string
  dateAfter?: string
  page?: number
}

const useClassAbsencesList = (params: Params, enabled = true) =>
  useQuery({
    queryKey: ['panelClassAttendancesList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelAttendancesList({
        semester_id: params.semester,
        absence_status: params.status,
        class_id: params.classId,
        course_id: params.courseId,
        student_id: params.studentId,
        date_after: params.dateAfter
          ? formatApiDate(new Date(params.dateAfter))
          : undefined,
        date_before: params.dateBefore
          ? formatApiDate(new Date(params.dateBefore))
          : undefined,
        page: params.page,
        page_size: DEFAULT_PAGE_SIZE,
        presence: [`absent_${params.presence}`],
        ordering: '-date,start_time',
        fetchKeys: {
          id: true,
          presence: true,
          lesson: true,
          student: true,
          absence: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return {
        list: parsed,
        count: response.count
      }
    },
    enabled
  })

export default useClassAbsencesList
